import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {concat, map, Observable, of, forkJoin} from 'rxjs';
import * as _ from 'lodash';
import {UrlService} from './url.service';
import {InitializationAndConfigurationService} from './configuration/initializationAndConfiguration.service';

@Injectable()
export class InstrumentService {
    private BASE_URL: string;
    private categoriesHelp = {};
    private instrumentVisibilityMap = {};
    private instruments = null;             // This is a short version of the instruments with minimal information.
    private fullInstruments = null;         // This is complete definition of the instruments including categories, questions, etc.
    private validationScriptsMap = null;
    private parsedCansRendererConfiguration: any = null;

    constructor(private http: HttpClient,
                private urlService: UrlService,
                private initializationAndConfigurationService: InitializationAndConfigurationService) {
        this.BASE_URL = this.urlService.getBackendUrl() + 'data/instruments';
    }

    getCategoryHelp(categoryHelpUrl) {
        if (this.categoriesHelp[categoryHelpUrl] != null) {
            return this.categoriesHelp[categoryHelpUrl];
        } else {
            let params = new HttpParams();
            params = params.set('uriPath', categoryHelpUrl);
            params = params.set('timestamp', new Date().getTime());

            const requestOptions = {
                params: params
            }

            this.categoriesHelp[categoryHelpUrl] = this.http.get(this.BASE_URL + '/help', requestOptions).pipe(
                map((res: any) => {
                    const categoryHelp = {};
                    if ((res != null) && (res.questions)) {
                        res.questions.forEach((value, key) => {
                            categoryHelp[value.url_path] = value;
                        });
                    }
                    return categoryHelp;
            }));

            return this.categoriesHelp[categoryHelpUrl];
        }
    }

    // Get the instrumentVisibilityRules for multiple instrumentTypes.
    // First check to see if all have been loaded already.  If some
    // are missing, set up a loop to call for each.  Once all those
    // calls return, return the full instrument visibility map.
    // getInstrumentVisibility(instrumentTypes) {
    //     return new Observable<any>(observer => {
    //         const missingTypes = [];
    //         instrumentTypes.forEach((instrumentType) => {
    //             if (this.instrumentVisibilityMap[instrumentType] == null) {
    //                 missingTypes.push(instrumentType);
    //             }
    //         });
    //
    //         if (missingTypes.length === 0) {
    //             observer.next(this.instrumentVisibilityMap);
    //             observer.complete();
    //         } else {
    //             const observerArray = [];
    //             missingTypes.forEach((missingType) => {
    //                 const params = {
    //                     'instrumentType': missingType
    //                 };
    //
    //                 observerArray.push(this.getInstrumentVisibilityInternal(missingType));
    //             });
    //
    //             concat(observerArray).subscribe({
    //                 complete: () => {
    //                     observer.next(this.instrumentVisibilityMap);
    //                     observer.complete();
    //                 },
    //                 error: (error) => {
    //                     console.log(error);
    //                     observer.error(error);
    //                 }
    //             });
    //         }
    //     });
    // }
    //
    // getInstrumentVisibilityInternal(instrumentType) {
    //     let params = new HttpParams();
    //     params = params.set('instrumentType', instrumentType);
    //     const requestOptions = {
    //         params: params
    //     }
    //
    //     return this.http.get(this.BASE_URL + '/visibility', requestOptions).pipe(
    //         map((result: any) => {
    //             if (result == null) {
    //                 result = {};
    //             }
    //
    //             const singleMap = {
    //                 domains: {}
    //                 , questions: {}
    //             };
    //             this.instrumentVisibilityMap[instrumentType] = singleMap;
    //
    //             result.domains.forEach((domain) => {
    //                 singleMap.domains[domain.identifier] = domain.visibility;
    //                 domain.questions.forEach((question) => {
    //                     singleMap.questions[question.identifier] = question.visibility;
    //                 });
    //             });
    //         }));
        // }

    getInstrumentVisibility(instrumentTypes: string[]): Observable<any> {
        // Check which types we need to fetch
        const missingTypes: string[] = [];
        instrumentTypes.forEach((instrumentType) => {
            if (this.instrumentVisibilityMap[instrumentType] == null) {
                missingTypes.push(instrumentType);
            }
        });

        // If we have everything cached, return immediately
        if (missingTypes.length === 0) {
            return of(this.instrumentVisibilityMap);
        }

        // Create observables for each missing type
        const requests = missingTypes.map((missingType) => {
            const params = new HttpParams().set('instrumentType', missingType);
            
            return this.http.get(this.BASE_URL + '/visibility', { params }).pipe(
                map((res: any) => {
                    if (res == null) {
                        res = {};
                    }

                    const singleMap = {
                        domains: {},
                        questions: {}
                    };
                    this.instrumentVisibilityMap[missingType] = singleMap;

                    if (res.domains) {
                        res.domains.forEach((domain: any) => {
                            singleMap.domains[domain.identifier] = domain.visibility;
                            domain.questions.forEach((question: any) => {
                                singleMap.questions[question.identifier] = question.visibility;
                            });
                        });
                    }

                    return singleMap;
                })
            );
        });

        // Use forkJoin to wait for all requests to complete
        return forkJoin(requests).pipe(
            map(() => {
                return this.instrumentVisibilityMap;
            })
        );
    }

    getFullInstruments() {
        if (this.fullInstruments != null) {
            return of(this.fullInstruments);
        } else {
            return this.http.get(this.BASE_URL).pipe(map(instruments => {
                this.fullInstruments = instruments;
                return this.fullInstruments;
            }));
        }
    }

    getInstruments() {
        if (this.instruments != null) {
            return of(this.instruments);
        } else {
            return this.http.get(this.BASE_URL + '/info').pipe(
                map(instruments => {
                    this.instruments = instruments;
                    return this.instruments;
                })
            );
        }
    }

    getInstrumentCategoriesByInstrumentId(instrumentId) {
        return this.http.get(this.BASE_URL + '/' + instrumentId + '/instrumentCategories');
    }

    getInstrumentByType(instrumentType) {
        if (this.instruments != null) {
            return of(this.findInstrumentByType(instrumentType));
        } else {
            return this.getInstruments().pipe(map(instruments => {
                return this.findInstrumentByType(instrumentType)
            }));
        }
    }

    // This is basically a Max function but just for the CANS answer set,
    // which is string values of 0, 1, 2, or 3.  The function will return the
    // maximum of these values.  It'll do it using string-based comparison,
    // rather than numeric computations to keep the typing consistent.
    //
    // It does default the max to '0', which may not always actually be the case
    // if none of the questions are answered, but I think it's good enough.
    getCansCategoryMax(category: any): string {
        let max: string = '0';

        if (!category || !category.questions) {
            return max;
        }

        category.questions.forEach((value, key) => {
            const answerValue = value && value.a && value.a[0] ? value.a[0] : null;
            if (answerValue === null || answerValue === undefined || answerValue === '') {
                return;
            }

            if (answerValue === '1') {
                if (max === '0') {
                    max = '1';
                }
            } else if ((answerValue === '2') || (answerValue === 'Y')) {
                if ((max === '0') || (max === '1')) {
                    max = '2';
                }
            } else if (answerValue === '3') {
                if (max === '0' || max === '1' || max === '2') {
                    max = '3';
                }
            }
        });

        return max;
    }

    /**
     * Get the minimum numeric value from a category's questions.
     * Returns null if no numeric values found.
     */
    getCansCategoryMin(category: any): string | null {
        let min: string | null = null;

        if (!category || !category.questions) {
            return null;
        }

        category.questions.forEach((value, key) => {
            const answerValue = value && value.a && value.a[0] ? value.a[0] : null;
            if (answerValue === null || answerValue === undefined || answerValue === '') {
                return;
            }

            if (answerValue === '0') {
                min = '0';
            } else if (answerValue === '1') {
                if (min === null || min === '2' || min === '3') {
                    min = '1';
                }
            } else if ((answerValue === '2') || (answerValue === 'Y')) {
                if (min === null || min === '3') {
                    min = '2';
                }
            } else if (answerValue === '3') {
                if (min === null) {
                    min = '3';
                }
            }
        });

        return min;
    }

    // /**
    //  * Get the maximum value (2, 3, or Y) from a category's questions.
    //  * Returns 0 if no values found.
    //  * Used for styling purposes.
    //  */
    // getCategoryMax(category: any): number {
    //     let max = 0;
    //
    //     category.questions.forEach((value: any) => {
    //         if (value.a[0] === '2') {
    //             if (max < 2) {
    //                 max = 2;
    //             }
    //         } else if (value.a[0] === '3') {
    //             if (max < 3) {
    //                 max = 3;
    //             }
    //         } else if (value.a[0] === 'Y') {
    //             if (max < 2) {
    //                 max = 2;
    //             }
    //         }
    //     });
    //
    //     return max;
    // }
    //
    /**
     * Get the parsed CANS renderer configuration from applicationConfiguration.
     * This is cached after first access.
     */
    private getCansRendererConfiguration(): any {
        if (this.parsedCansRendererConfiguration !== null) {
            return this.parsedCansRendererConfiguration;
        }

        const applicationConfiguration = this.initializationAndConfigurationService.getApplicationConfiguration();
        if (!applicationConfiguration) {
            this.parsedCansRendererConfiguration = {};
            return this.parsedCansRendererConfiguration;
        }

        const rawConfig = applicationConfiguration.cansRendererConfiguration;

        if (!rawConfig) {
            this.parsedCansRendererConfiguration = {};
            return this.parsedCansRendererConfiguration;
        }

        if (typeof rawConfig === 'string') {
            try {
                this.parsedCansRendererConfiguration = JSON.parse(rawConfig);
            } catch (error) {
                console.warn('Unable to parse cansRendererConfiguration configuration', error);
                this.parsedCansRendererConfiguration = {};
            }
        } else {
            this.parsedCansRendererConfiguration = rawConfig;
        }

        return this.parsedCansRendererConfiguration;
    }

    /**
     * Get the classification (e.g., 'STRENGTH') for a category from the CANS renderer configuration.
     */
    getCategoryClassification(category: any, instrumentType: string): string | null {
        if (!category || !category.text || !instrumentType) {
            return null;
        }

        const cansRendererConfiguration = this.getCansRendererConfiguration();
        if (!cansRendererConfiguration || !cansRendererConfiguration[instrumentType]) {
            return null;
        }

        const categoryConfig = cansRendererConfiguration[instrumentType][category.text];
        return categoryConfig ? categoryConfig.classification : null;
    }

    getCategoryDomainCode(category: any, instrumentType: string): string | null {
        if (!category || !category.text || !instrumentType) {
            return null;
        }

        const cansRendererConfiguration = this.getCansRendererConfiguration();
        if (!cansRendererConfiguration || !cansRendererConfiguration[instrumentType]) {
            return null;
        }

        const categoryConfig = cansRendererConfiguration[instrumentType][category.text];
        return categoryConfig ? categoryConfig.code : null;
    }

    /**
     * Get the category type (e.g., 'success', 'warning', 'danger', 'default') based on:
     * - Category classification (STRENGTH vs NEED)
     * - Answer values in the category
     * - Instrument type for configuration lookup
     */
    getCategoryType(category: any, instrumentType?: string): string {
        const classification = this.getCategoryClassification(category, instrumentType || (category && category.instrumentType));

        if (classification === 'STRENGTH') {
            const min = this.getCansCategoryMin(category);
            // Handle null case - if no values found, return default
            if (min === null) {
                return 'default';
            }
            if (min === '0') {
                return 'success';
            }
            return 'default';
        }

        switch (this.getCansCategoryMax(category)) {
            case '1':
                return 'info';
            case '2':
                return 'warning';
            case '3':
                return 'danger';
        }
        return 'default';
    }

    isCategoryVisible(category, filterValues, textFilter) {
        if ((filterValues == null || filterValues.length === 0)
            && !(textFilter)) {
            return true;
        }

        let textVisible = true;
        if (textFilter) {
            textVisible = false;
            _.every(category.questions, function (question) {
                if (question.text.toLowerCase().indexOf(textFilter.toLowerCase()) !== -1) {
                    textVisible = true;
                    return false;
                }
                return true;
            });
        }

        let valueVisible = true;
        if (!(filterValues == null || filterValues.length === 0)) {
            valueVisible = false;
            const max = this.getCansCategoryMax(category);
            _.every(filterValues, (value) => {
                if (max === value) {
                    valueVisible = true;
                    return false;
                }
                return true;
            });
        }

        return textVisible && valueVisible;
    }

    getValidationScriptsMap() {
        if (this.validationScriptsMap) {
            return of(this.validationScriptsMap);
        } else {
            return this.http.get(this.BASE_URL + '/validationScriptsMap').pipe(
                map((res) => {
                    this.validationScriptsMap = res;
                    return this.validationScriptsMap;
                })
            );
        }
    }

    private findInstrumentByType(instrumentType) {
        if (this.instruments != null) {
            return _.filter(this.instruments, {instrumentType: instrumentType});
        }
        return null;
    };
}
